import { type ComponentType, createContext, type JSX, useContext } from "react";
import type { TaskProps } from "../../types/TaskProps";

interface TaskPermissions {
  /** The user may complete (user task) or trigger (message task) the task. */
  canAct: boolean;
  canSave: boolean;
}

// Outside a task view nothing is restricted.
const TaskPermissionsContext = createContext<TaskPermissions>({
  canAct: true,
  canSave: true
});

export function useTaskPermissions(): TaskPermissions {
  return useContext(TaskPermissionsContext);
}

/**
 * Passes the actions flow-process allows on the task down to the task view, so the shared
 * task buttons are disabled (not hidden) when the user may not use them, e.g. reader roles.
 */
export function withTaskPermissions(
  Component: ComponentType<TaskProps>
): ComponentType<TaskProps> {
  const WithTaskPermissions = (props: TaskProps): JSX.Element => (
    <TaskPermissionsContext.Provider
      value={{
        canAct: props.canComplete || props.canTrigger,
        canSave: props.canSave
      }}
    >
      <Component {...props} />
    </TaskPermissionsContext.Provider>
  );
  WithTaskPermissions.displayName = `withTaskPermissions(${Component.displayName ?? Component.name})`;

  return WithTaskPermissions;
}
